import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Parsing JavaScript with JavaScript",
  "description": "",
  "published": "2011-11-11T00:00:00.000Z",
  "redirect_from": ["/blog/parsing-javascript-with-javascript/swizec/2909"],
  "categories": "Abstract syntax tree, Application programming interface, AST, Compilers, JavaScript, Languages, Parsing, Programming, Uncategorized",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`Over the weekend I started working on `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/llamaduck"
      }}>{`llamaduck`}</a>{`- a simple tool that aims to figure out whether your code will run on the newly released node 0.6.0. Eventually it might be able to perform other compatibility assessment tasks as well, but I'm focusing on simple stuff first.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2011/11/6334305146_57b7976719_m1.jpg",
        "alt": "southridge-56gold-111105-891.JPG",
        "title": "southridge-56gold-111105-891.JPG"
      }}></img></p>
    <p>{`Or at least I thought it was simple.`}</p>
    <p>{`The `}<a parentName="p" {...{
        "href": "https://github.com/joyent/node/wiki/API-changes-between-v0.4-and-v0.6"
      }}>{`list of API changes since 0.4.x`}</a>{` doesn't seem that long and it should be easy enough to digest. But as it turns out, I spent almost all of Sunday just figuring out how to turn javascript into a beautiful analyzable `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Abstract_syntax_tree",
        "title": "Abstract syntax tree"
      }}>{`AST`}</a>{`.`}</p>
    <p>{`If you don't know what an AST is - it's a so called abstract syntax tree, which means it should look identical regardless of what the actual syntax is. Although it will differ for actually different languages. So a coffeescript AST should look the same as JavaScript, but Python's will differ.`}</p>
    <p>{`My research came up with three options:`}</p>
    <ol>
      <li parentName="ol">{`Take a parser generator and a JavaScript grammar, hope for the best`}</li>
      <li parentName="ol"><a parentName="li" {...{
          "href": "http://www.jslint.com/",
          "title": "JSLint"
        }}>{`JSLint`}</a>{` has a parser ... somewhere around line 2000`}</li>
      <li parentName="ol">{`Uglify-JS supposedly has a parser too`}</li>
    </ol>
    <p>{`The only viable option was uglify-js. It's a neatly packaged node.js module that does a bit more than I need, but at least it's got an easy to use parser with an exposed api interface.`}</p>
    <h2 {...{
      "id": "score"
    }}>{`Score!`}</h2>
    <p>{`Here's an example of a file that outputs its own AST to give you a feel for what I'm talking about:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`var parser = require("uglify-js").parser;
var util = require("util");

(function get_ast(path, callback) {
  require("fs").readFile(path, "utf-8", function (err, data) {
    if (err) throw err;

    callback(parser.parse(data));
  });
})("./example.js", function (data) {
  console.log(util.inspect(data, true, null));
});
`}</code></pre>
    <p>{`The file parses itself and outputs a tree encoded as a javascript array (scroll past the insanity, there's a bit more text there):`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`[
  "toplevel",
  [
    [
      "var",
      [
        [
          "parser",
          [
            "dot",
            [
              "call",
              ["name", "require", ([length]: 2)],
              [["string", "uglify-js", ([length]: 2)], ([length]: 1)],
              ([length]: 3),
            ],
            "parser",
            ([length]: 3),
          ],
          ([length]: 2),
        ],
        ([length]: 1),
      ],
      ([length]: 2),
    ],
    [
      "var",
      [
        [
          "util",
          [
            "call",
            ["name", "require", ([length]: 2)],
            [["string", "util", ([length]: 2)], ([length]: 1)],
            ([length]: 3),
          ],
          ([length]: 2),
        ],
        ([length]: 1),
      ],
      ([length]: 2),
    ],
    [
      "stat",
      [
        "call",
        [
          "function",
          "get_ast",
          ["path", "callback", ([length]: 2)],
          [
            [
              "stat",
              [
                "call",
                [
                  "dot",
                  [
                    "call",
                    ["name", "require", ([length]: 2)],
                    [["string", "fs", ([length]: 2)], ([length]: 1)],
                    ([length]: 3),
                  ],
                  "readFile",
                  ([length]: 3),
                ],
                [
                  ["name", "path", ([length]: 2)],
                  ["string", "utf-8", ([length]: 2)],
                  [
                    "function",
                    null,
                    ["err", "data", ([length]: 2)],
                    [
                      [
                        "if",
                        ["name", "err", ([length]: 2)],
                        [
                          "throw",
                          ["name", "err", ([length]: 2)],
                          ([length]: 2),
                        ],
                        undefined,
                        ([length]: 4),
                      ],
                      [
                        "stat",
                        [
                          "call",
                          ["name", "callback", ([length]: 2)],
                          [
                            [
                              "call",
                              [
                                "dot",
                                ["name", "parser", ([length]: 2)],
                                "parse",
                                ([length]: 3),
                              ],
                              [["name", "data", ([length]: 2)], ([length]: 1)],
                              ([length]: 3),
                            ],
                            ([length]: 1),
                          ],
                          ([length]: 3),
                        ],
                        ([length]: 2),
                      ],
                      ([length]: 2),
                    ],
                    ([length]: 4),
                  ],
                  ([length]: 3),
                ],
                ([length]: 3),
              ],
              ([length]: 2),
            ],
            ([length]: 1),
          ],
          ([length]: 4),
        ],
        [
          ["string", "./example.js", ([length]: 2)],
          [
            "function",
            null,
            ["data", ([length]: 1)],
            [
              [
                "stat",
                [
                  "call",
                  [
                    "dot",
                    ["name", "console", ([length]: 2)],
                    "log",
                    ([length]: 3),
                  ],
                  [
                    [
                      "call",
                      [
                        "dot",
                        ["name", "util", ([length]: 2)],
                        "inspect",
                        ([length]: 3),
                      ],
                      [
                        ["name", "data", ([length]: 2)],
                        ["name", "true", ([length]: 2)],
                        ["name", "null", ([length]: 2)],
                        ([length]: 3),
                      ],
                      ([length]: 3),
                    ],
                    ([length]: 1),
                  ],
                  ([length]: 3),
                ],
                ([length]: 2),
              ],
              ([length]: 1),
            ],
            ([length]: 4),
          ],
          ([length]: 2),
        ],
        ([length]: 3),
      ],
      ([length]: 2),
    ],
    ([length]: 3),
  ],
  ([length]: 2),
];
`}</code></pre>
    <h2 {...{
      "id": "conclusion"
    }}>{`Conclusion`}</h2>
    <p>{`Now we have a simple tree we can recursively analyze and look for incompatibilities. But before anything really practical can be done I need to figure out how to track `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Scope_%28programming%29",
        "title": "Scope (programming)"
      }}>{`variable scope`}</a>{`. That's really the hard bit because the code needs to check when variables become a critical section and then confirm that they do in fact eventually get used in a critical way.`}</p>
    <p>{`But once that nut is cracked `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/llamaduck"
      }}>{`llamaduck`}</a>{` will be a neat little tool useful for many things.`}</p>
    <p>{`If you've got some coding inclination, I'd love a helping hand over at the `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/llamaduck"
      }}>{`llamaduck github repo`}</a>{`.`}</p>
    <h6 {...{
      "id": "related-articles"
    }}>{`Related articles`}</h6>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://coding.smashingmagazine.com/2011/10/27/lessons-from-a-review-of-javascript-code/"
        }}>{`Lessons From A Review Of JavaScript Code`}</a>{` (coding.smashingmagazine.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://leuksman.com/log/2011/09/29/parserplayground-partial-internals-update/"
        }}>{`ParserPlayground partial internals update`}</a>{` (leuksman.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://googleclosure.wordpress.com/2011/08/27/google-closure-introduction/"
        }}>{`Google Closure Introduction`}</a>{` (googleclosure.wordpress.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://olabini.com/blog/2011/11/announcing-jescov-javascript-code-coverage/"
        }}>{`Announcing JesCov - JavaScript code coverage`}</a>{` (olabini.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://www.mindscapehq.com/blog/index.php/2011/07/21/polyglot-programming-some-lessons-learned/"
        }}>{`Polyglot programming - combining functional, dynamic and imperative languages`}</a>{` (mindscapehq.com)`}</li>
    </ul>
    <p><a parentName="p" {...{
        "href": "http://www.zemanta.com/",
        "title": "Enhanced by Zemanta"
      }}><img parentName="a" {...{
          "src": "https://img.zemanta.com/zemified_e.png?x-id=e0fdf526-3ca2-4370-ba22-f3f24a2f86af",
          "alt": "Enhanced by Zemanta"
        }}></img></a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      